import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { currentLang, setLang, t, type Lang } from '../core/i18n';
import { hasSave } from '../core/store';
import { initAudio } from '../core/audio';
import './game';

@customElement('tpe-landing')
export class TpeLanding extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private langCode: Lang = currentLang();
  @state() private existing = hasSave();

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('tpe:lang-change', this.onLang);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('tpe:lang-change', this.onLang);
  }
  private onLang = (e: Event): void => {
    this.langCode = (e as CustomEvent<Lang>).detail;
    this.requestUpdate();
  };

  private pick(l: Lang): void {
    setLang(l);
  }

  private begin(): void {
    initAudio();
    const card = this.querySelector('.landing-card') as HTMLElement | null;
    if (card) {
      card.style.transition = 'opacity 700ms ease, transform 700ms ease';
      card.style.opacity = '0';
      card.style.transform = 'translateY(-12px)';
    }
    setTimeout(() => {
      const slot = document.getElementById('app');
      if (!slot) return;
      slot.innerHTML = '';
      const game = document.createElement('tpe-game');
      (game as HTMLElement).style.display = 'block';
      (game as HTMLElement).style.height = '100%';
      slot.appendChild(game);
      document.body.style.overflow = 'auto';
    }, 750);
  }

  render() {
    const isPt = this.langCode === 'pt-br';
    return html`
      <div class="relative z-10 h-full w-full grid place-items-center px-6">
        <div class="lang-toggle absolute top-4 right-5 flex items-center gap-2 z-20">
          <button aria-pressed=${!isPt} @click=${() => this.pick('en')}>${t('lang.en')}</button>
          <span aria-hidden="true" class="opacity-30">·</span>
          <button aria-pressed=${isPt} @click=${() => this.pick('pt-br')}>${t('lang.pt')}</button>
        </div>
        <div class="phi absolute top-4 left-5 z-20" aria-hidden="true">${t('landing.phi')}</div>

        <div class="landing-card text-center max-w-2xl animate-fade-in">
          <div class="divider-ornate w-72 mx-auto mb-6">· ${isPt ? 'Prólogo' : 'Prologue'} ·</div>
          <h1
            class="title text-4xl md:text-6xl mb-4 animate-glow"
            style="color:var(--ink); text-shadow: 0 2px 0 rgba(255,240,200,.4)"
          >
            ${t('landing.title')}
          </h1>
          <p
            class="italic text-lg md:text-xl max-w-xl mx-auto mb-10 animate-float"
            style="color:var(--ink-soft)"
          >
            ${t('landing.subtitle')}
          </p>

          <button class="btn btn-corners" @click=${this.begin}>
            ${this.existing ? t('landing.continue') : t('landing.begin')}
          </button>

          <p class="mt-8 text-xs opacity-50 font-mono tracking-wider">${t('landing.note')}</p>
        </div>
      </div>

      <svg
        class="absolute -z-0 top-10 left-10 w-24 h-24 opacity-30 animate-float"
        viewBox="0 0 64 64"
        fill="none"
        stroke="${unsafeCSS('var(--accent)')}"
        stroke-width="1"
        aria-hidden="true"
      >
        <circle cx="32" cy="32" r="28" />
        <circle cx="32" cy="32" r="20" />
        <path d="M32 4v56 M4 32h56 M12 12l40 40 M52 12 12 52" />
      </svg>
      <svg
        class="absolute -z-0 bottom-10 right-10 w-24 h-24 opacity-30 animate-float"
        style="animation-delay:1.5s"
        viewBox="0 0 64 64"
        fill="none"
        stroke="${unsafeCSS('var(--accent)')}"
        stroke-width="1"
        aria-hidden="true"
      >
        <polygon points="32,4 60,32 32,60 4,32" />
        <polygon points="32,14 50,32 32,50 14,32" />
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tpe-landing': TpeLanding;
  }
}
